import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import DashboardShell from 'src/features/dashboard/components/DashboardShell';
import SectionCard from 'src/features/dashboard/components/SectionCard';
import QuickActions from 'src/features/dashboard/components/QuickActions';
import RiskHeatMini from 'src/features/dashboard/components/RiskHeatMini';
import ScurvePanel from 'src/features/dashboard/components/ScurvePanel';
import ProgressOverview from 'src/features/dashboard/components/ProgressOverview';
import WbsLevel1Summary from 'src/features/dashboard/components/WbsLevel1Summary';
import UpcomingDeadlines from 'src/features/dashboard/components/UpcomingDeadlines';

import { useDashboardV2Snapshot } from 'src/features/dashboard/hooks/useDashboardSnapshot';
import AsOfSelector from 'src/features/wbs/components/overview/AsOfSelector';
import { dowLabelKoShort } from 'src/utils/date';

import type { WbsViewAs, WbsEvViewMode } from 'src/features/wbs/components/overview/AsOfSelector';
import type { UpcomingDeadlineItem } from 'src/features/dashboard/api/dashboard.api';
import type { CashflowMiniRow } from 'src/features/dashboard/components/CashflowMini';

function ProjectKeyInfo({
    contractAmountText,
    scheduleText,
    clientText,
    currencyText,
    summaryText,
    summaryMeta,
}: {
    contractAmountText: string;
    scheduleText: string;
    clientText?: string;
    currencyText?: string;
    summaryText: string;
    summaryMeta: string;
}) {
    return (
        <div className="rounded-[24px] border border-slate-200/80 bg-white/96 p-5 shadow-[0_16px_40px_-26px_rgba(15,23,42,0.26)]">
            <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold tracking-tight text-slate-900">프로젝트 핵심 정보</div>
                <div className="text-xs text-slate-500">계약 및 운영 기준</div>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                    <div className="text-xs uppercase tracking-[0.14em] text-slate-500">계약금액</div>
                    <div className="mt-2 text-lg font-semibold tracking-tight text-slate-950">{contractAmountText}</div>
                    {currencyText && <div className="mt-1 text-xs text-slate-500">{currencyText}</div>}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                    <div className="text-xs uppercase tracking-[0.14em] text-slate-500">일정</div>
                    <div className="mt-2 text-sm font-semibold text-slate-950">{scheduleText}</div>
                    <div className="mt-1 text-xs text-slate-500">기준: WBS 계획 일정</div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                    <div className="text-xs uppercase tracking-[0.14em] text-slate-500">고객</div>
                    <div className="mt-2 text-sm font-semibold text-slate-950">{clientText ?? '—'}</div>
                    <div className="mt-1 text-xs text-slate-500">계약 기준</div>
                </div>

                <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 p-4">
                    <div className="text-xs uppercase tracking-[0.14em] text-amber-700">현황 요약</div>
                    <div className="mt-2 text-sm font-semibold text-slate-950">{summaryText}</div>
                    <div className="mt-1 text-xs text-slate-600">{summaryMeta}</div>
                </div>
            </div>
        </div>
    );
}

export default function ProjectDashboardClassic() {
    const { projectId } = useParams();
    const pid = projectId as string;

    const nav = useNavigate();

    const [viewAs, setViewAs] = useState<WbsViewAs>('WEEK');
    const [baseDate, setBaseDate] = useState<Date>(new Date());
    const [evViewMode, setEvViewMode] = useState<WbsEvViewMode>('CONFIRMED_W1');

    const asOf = useMemo(() => {
        const y = baseDate.getFullYear();
        const m = String(baseDate.getMonth() + 1).padStart(2, '0');
        const d = String(baseDate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, [baseDate]);

    const { data, isLoading, isError } = useDashboardV2Snapshot(projectId, {
        asOf,
        viewAs,
        evBasis: evViewMode,
    });

    const periodText = useMemo(() => {
        const s = data?.project.startDate ?? null;
        const e = data?.project.endDate ?? null;
        return s && e ? `${s} ~ ${e}` : '—';
    }, [data?.project.startDate, data?.project.endDate]);

    const contractAmountText = useMemo(() => {
        const raw = data?.project.contractPrice;
        const n = Number((raw as any)?.toString?.() ?? raw);
        if (!Number.isFinite(n) || n <= 0) return '—';
        const cur = data?.project.contractCurrency ?? '';
        return `${cur ? `${cur} ` : ''}${n.toLocaleString()}`;
    }, [data?.project.contractPrice, data?.project.contractCurrency]);

    const allDeadlines = useMemo<UpcomingDeadlineItem[]>(() => {
        const b = (data as any)?.upcomingDeadlinesByKind as
            | { task: UpcomingDeadlineItem[]; risk: UpcomingDeadlineItem[]; payment: UpcomingDeadlineItem[] }
            | undefined;

        const merged = [...(b?.task ?? []), ...(b?.risk ?? []), ...(b?.payment ?? [])];

        return merged
            .filter((x) => !!x?.dueDate)
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
            .slice(0, 6);
    }, [data]);

    const cashflowMini = useMemo<{ data: CashflowMiniRow[]; currency?: string | null } | undefined>(() => {
        const po = data?.paymentOverview;
        const rows = po?.monthly ?? [];

        const mapped: CashflowMiniRow[] = rows.map((r) => ({
            month: r.month,
            netCum: Number(r.netCum ?? 0),
        }));

        return {
            data: mapped,
            currency: po?.currency ?? data?.project.contractCurrency ?? null,
        };
    }, [data]);

    const canCreateReport = useMemo(() => {
        if (!data) return false;

        const hasCurrentWbs = data.wbsListVersion != null;
        const hasWbsRows = (data.wbsLevel1Rows?.length ?? 0) > 0;
        const hasScurveRows = (data.scurveRows?.length ?? 0) > 0;

        return hasCurrentWbs && (hasWbsRows || hasScurveRows);
    }, [data]);

    const delayCount = useMemo(
        () => (data?.wbsLevel1Rows ?? []).filter((row) => row.status === 'AT_RISK').length,
        [data?.wbsLevel1Rows],
    );

    const riskCount = data?.risks?.length ?? 0;
    const deadlineCount = allDeadlines.length;
    const summaryText = delayCount > 0 ? `${delayCount}개 공정 주의` : '지연 신호 낮음';
    const summaryMeta = `리스크 ${riskCount}건 / 예정 마감 ${deadlineCount}건`;

    if (isLoading) {
        return (
            <DashboardShell
                title="Dashboard"
                subtitle="Dashboard snapshot"
                right={
                    <QuickActions
                        projectId={pid}
                        canCreateReport={false}
                        reportDisabledReason="리포트를 생성할 최소한의 정보가 부족합니다. WBS를 먼저 입력하세요."
                        asOf={asOf}
                        evBasis={evViewMode}
                    />
                }
            >
                <div className="mt-6 rounded-2xl border bg-white p-6 text-sm text-gray-600">불러오는 중...</div>
            </DashboardShell>
        );
    }

    if (isError || !data) {
        return (
            <DashboardShell
                title="Dashboard"
                subtitle="Dashboard snapshot"
                right={
                    <QuickActions
                        projectId={pid}
                        canCreateReport={false}
                        reportDisabledReason="리포트를 생성할 최소한의 정보가 부족합니다. WBS를 먼저 입력하세요."
                        asOf={asOf}
                        evBasis={evViewMode}
                    />
                }
            >
                <div className="mt-6 rounded-2xl border bg-white p-6 text-sm text-rose-700">
                    Dashboard snapshot을 불러오지 못했습니다.
                </div>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell
            title={
                <div className="flex flex-wrap items-center gap-2">
                    <span>{data.project.name}</span>

                    {data.wbsListVersion != null && (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                            WBS V{data.wbsListVersion} (current)
                        </span>
                    )}
                </div>
            }
            subtitle={
                <div className="flex flex-wrap gap-2">
                    <span>{periodText}</span>
                </div>
            }
            right={
                <QuickActions
                    projectId={pid}
                    canCreateReport={canCreateReport}
                    reportDisabledReason="리포트를 생성할 최소한의 정보가 부족합니다. WBS를 먼저 입력하세요."
                    asOf={asOf}
                    evBasis={evViewMode}
                />
            }
        >
            <div className="mt-2">
                <ProjectKeyInfo
                    contractAmountText={contractAmountText}
                    scheduleText={periodText}
                    clientText={data.project.clientName ?? undefined}
                    currencyText={data.project.contractCurrency ?? undefined}
                    summaryText={summaryText}
                    summaryMeta={summaryMeta}
                />
            </div>

            <div className="mt-6">
                <div className="mb-4 rounded-[24px] border border-slate-200/80 bg-white/96 p-4 shadow-[0_16px_40px_-26px_rgba(15,23,42,0.26)]">
                    <AsOfSelector
                        viewAs={viewAs}
                        baseDate={baseDate}
                        onChangeViewAs={setViewAs}
                        onChangeBaseDate={setBaseDate}
                        evViewMode={evViewMode}
                        onChangeEvViewMode={setEvViewMode}
                        liveAvailable={data?.liveAvailable ?? false}
                        weekOfISO={data?.weekOfISO}
                        anchorDowLabel={data?.anchorDow != null ? dowLabelKoShort(data.anchorDow) : undefined}
                    />
                </div>
                {data.approvalNotice && (
                    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {data.approvalNotice.message}
                    </div>
                )}
                <ProgressOverview
                    model={data.progressCashflow}
                    cashflowMini={cashflowMini}
                    onOpenPayments={() => nav(`/projects/${data.project.id}/payments`)}
                />
            </div>

            <div className="mt-6">
                <WbsLevel1Summary
                    rows={data.wbsLevel1Rows}
                    wbsTo={`/projects/${data.project.id}/wbs`}
                    wbsListVersion={data.wbsListVersion}
                />
            </div>

            <div className="mt-6">
                <ScurvePanel rows={data.scurveRows} mode="cum+inc" linkTo={`/projects/${data.project.id}/s-curve`} />
            </div>

            <div className="mt-6 grid grid-cols-1 items-stretch gap-4 2xl:grid-cols-2">
                <div>
                    <SectionCard title="Upcoming Deadlines" right={<span className="text-xs text-gray-500">Top 6</span>}>
                        <UpcomingDeadlines items={allDeadlines} buckets={data.upcomingDeadlinesByKind} />
                    </SectionCard>
                </div>

                <div className="lg:col-span-1">
                    <SectionCard title="Risk Snapshot" right={<span className="text-xs text-gray-500">5×5</span>}>
                        <RiskHeatMini risks={data.risks} />
                    </SectionCard>
                </div>
            </div>
        </DashboardShell>
    );
}
